import { useCallback, useRef, useState } from "react";
import {
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { UploadFile } from "@mui/icons-material";
import { IMIMEType } from "../../interfaces/IMIMEType";
import { useDropzone } from "react-dropzone";
import { deleteFetch, uploadFetch, useFetch } from "../../hooks/useFetch";
import { OfferPositionCheckListItemDocumentRow } from "./OfferPositionCheckListItemDocumentRow";
import { IOfferPositionCheckListItemDocument } from "../../interfaces/IProjectDocument";
import { IOfferPositionCheckListItem } from "../../interfaces/IOfferPositionCheckListItem";
import { CameraAlt } from "@mui/icons-material";

interface IProps {
    checkListItem: IOfferPositionCheckListItem;
    checkListItems: IOfferPositionCheckListItem[];
    setCheckListItems: Function;
    onUploadStatusChange: (uploading: boolean) => void;
}

export const OfferPositionCheckListItemDocumentsOverview: React.FC<IProps> = ({
    checkListItem,
    checkListItems,
    setCheckListItems,
    onUploadStatusChange,
}) => {
    const inputUpload = useRef<HTMLInputElement | null>(null);
    const cameraInput = useRef<HTMLInputElement | null>(null);

    const [mimeTypeArray] = useFetch<IMIMEType[]>("/document/mimetype/");
    const [isUploading, setIsUploading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);

    const handleFilesUpload = useCallback(
        async (acceptedFiles: File[]) => {
            if (acceptedFiles.length === 0 || mimeTypeArray === undefined) return;

            setLoadingText("Dokument wird hochgeladen...");
            setIsUploading(true);
            onUploadStatusChange(true);

            const uploadPromises = acceptedFiles.map((file) => {
                return new Promise<IOfferPositionCheckListItemDocument>(async (resolve, reject) => {
                    const mimeType = mimeTypeArray.find((y) => y.MIMEtype === file.type);
                    if (!mimeType) {
                        reject(new Error("MIME type not supported"));
                        return;
                    }

                    const data = await fileToBase64(file);
                    const document = {
                        id: null,
                        file_name: file.name,
                        MIMEtype: mimeType,
                        data,
                    };

                    uploadFetch(
                        "/offerpositionchecklistitem/document/",
                        true,
                        { Document: document, OfferPositionCheckListItem: checkListItem.id },
                        (offerPositionCheckListItemDocument: IOfferPositionCheckListItemDocument) => {
                            if (offerPositionCheckListItemDocument) {
                                resolve(offerPositionCheckListItemDocument);
                            } else {
                                reject(new Error("Upload failed"));
                            }
                        },
                    );
                });
            });

            try {
                const results = await Promise.all(uploadPromises);

                setCheckListItems(
                    checkListItems.map((item) =>
                        item.id === checkListItem.id
                            ? { ...checkListItem, document_objects: [...checkListItem.document_objects, ...results] }
                            : item,
                    ),
                );
            } catch (error) {
                console.error("Upload error:", error);
            } finally {
                setIsUploading(false);
                onUploadStatusChange(false);
            }
        },
        [mimeTypeArray, checkListItem],
    );
    const handleDelete = useCallback(
        async (doc: IOfferPositionCheckListItemDocument) => {
            setLoadingText("Dokument wird gelöscht...");
            setIsUploading(true);
            onUploadStatusChange(true);

            const deletePromise = new Promise((resolve, reject) => {
                if (doc.id === null) {
                    reject(new Error("Document ID is null"));
                    return;
                }
                deleteFetch(`/offerpositionchecklistitem/document/`, doc.id, (deleted: boolean) => {
                    if (deleted) {
                        resolve(true);
                    } else {
                        reject(new Error("Delete failed"));
                    }
                });
            });

            try {
                const result = await deletePromise;
                if (!result) {
                    return;
                }

                setCheckListItems(
                    checkListItems.map((item) =>
                        item.id === checkListItem.id
                            ? {
                                  ...checkListItem,
                                  document_objects: checkListItem.document_objects.filter(
                                      (document) => document.id !== doc.id,
                                  ),
                              }
                            : item,
                    ),
                );
            } catch (error) {
                return;
            } finally {
                setIsUploading(false);
                onUploadStatusChange(false);
            }
        },
        [checkListItem],
    );

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            handleFilesUpload(acceptedFiles);
        },
        [checkListItem.document_objects],
    );
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        noClick: true,
    });

    if (mimeTypeArray === undefined) {
        return <CircularProgress />;
    }

    async function fileToBase64(file: File) {
        let result_base64 = await new Promise((resolve) => {
            let fileReader = new FileReader();
            fileReader.onload = (e) => resolve(fileReader.result);
            fileReader.readAsDataURL(file);
        });
        return String(result_base64).split(",")[1];
    }
    const handleNormalUpload = (files: null | FileList) => {
        if (files) {
            const acceptedFiles: File[] = Array.from(files);
            handleFilesUpload(acceptedFiles);
        }
    };
    const handleCameraUpload = (files: null | FileList) => {
        if (files && files[0]) {
            const file = files[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImage(reader.result as string);
                setPreviewFile(file);
                setShowPreviewDialog(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const confirmUpload = () => {
        if (previewFile) {
            handleFilesUpload([previewFile]);
        }
        setShowPreviewDialog(false);
        setPreviewImage(null);
        setPreviewFile(null);
    };

    const cancelUpload = () => {
        setShowPreviewDialog(false);
        setPreviewImage(null);
        setPreviewFile(null);
    };

    const dropZoneTSX = () => {
        return (
            <div>
                <input
                    {...getInputProps()}
                    accept={String(mimeTypeArray.map((mimeType) => mimeType.MIMEtype).join(", "))}
                />

                <Card>
                    <CardContent>
                        <Typography variant="body2">Dokument(e) hier reinziehen</Typography>
                        Erlaubte Datentypen
                        <br />
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Datentyp</TableCell>
                                    <TableCell>Datenendung</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {mimeTypeArray?.map((mimeType) => (
                                    <TableRow>
                                        <TableCell>{mimeType?.MIMEtype}</TableCell>
                                        <TableCell>{mimeType.file_ending}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        );
    };

    return (
        <div {...getRootProps()}>
            <input
                hidden
                ref={inputUpload}
                type="file"
                accept={String(mimeTypeArray.map((mimeType) => mimeType.MIMEtype).join(", "))}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleNormalUpload(event.target.files)}
            />
            <input
                hidden
                ref={cameraInput}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleCameraUpload(event.target.files)}
            />

            <Typography variant={"h5"} sx={{ mb: 2, mt: 0 }}>
                <IconButton
                    disabled={isUploading}
                    title="Daten über Auswähl hochladen"
                    onClick={() => inputUpload && inputUpload.current?.click()}
                    sx={{ float: "right" }}
                >
                    <UploadFile />
                </IconButton>
                <IconButton
                    sx={{ float: "right" }}
                    disabled={isUploading}
                    title="Foto aufnehmen"
                    onClick={() => cameraInput.current?.click()}
                >
                    <CameraAlt />
                </IconButton>
                <>
                    <Typography variant="caption">
                        (Hier können Sie Ihre Dokumente per <i>Drag & Drop</i> hochladen)
                    </Typography>
                </>
            </Typography>

            {showPreviewDialog && previewImage && (
                <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1">Vorschau des Fotos</Typography>
                    <img
                        src={previewImage}
                        alt="Vorschau"
                        style={{ maxWidth: "100%", maxHeight: 300, marginTop: 10 }}
                    />
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item>
                            <Button variant="contained" color="primary" onClick={confirmUpload}>
                                Hochladen
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button variant="outlined" color="secondary" onClick={cancelUpload}>
                                Abbrechen
                            </Button>
                        </Grid>
                    </Grid>
                </Card>
            )}

            {isDragActive && dropZoneTSX()}

            <>
                {isUploading ? (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12} display={"flex"} alignContent={"center"} justifyContent={"center"}>
                                <CircularProgress />
                            </Grid>
                            <Grid item xs={12} display={"flex"} alignContent={"center"} justifyContent={"center"}>
                                <Typography>{loadingText}</Typography>
                            </Grid>
                        </Grid>
                    </>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width={"20%"}>Nr.</TableCell>
                                <TableCell width={"60%"}>Dateiname</TableCell>
                                <TableCell width={"20%"}>Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {checkListItem.document_objects.map((document) => (
                                <OfferPositionCheckListItemDocumentRow
                                    currentDocument={document}
                                    mimeTypeArray={mimeTypeArray}
                                    deleteCheckListItemDocument={handleDelete}
                                />
                            ))}
                        </TableBody>
                    </Table>
                )}
            </>
        </div>
    );
};
